// Small, allocation-light vector and matrix helpers.
// Mat3 is row-major: m[r * 3 + c]. Frames used across the app:
//   EQJ  J2000 equatorial (x → RA 0h, z → north celestial pole)
//   ENU  local horizon (x → east, y → north, z → zenith)
//   CAM  camera (x → screen right, y → screen up, z → towards the viewer; the view looks along −z)

export type Vec3 = [number, number, number];
export type Mat3 = Float64Array;

export const DEG = Math.PI / 180;
export const RAD = 180 / Math.PI;

export const clamp = (v: number, lo: number, hi: number) => (v < lo ? lo : v > hi ? hi : v);
export const lerp = (a: number, b: number, t: number) => a + (b - a) * t;
export const smoothstep = (e0: number, e1: number, x: number) => {
  const t = clamp((x - e0) / (e1 - e0), 0, 1);
  return t * t * (3 - 2 * t);
};
export const wrap360 = (a: number) => ((a % 360) + 360) % 360;
export const wrap180 = (a: number) => {
  const w = wrap360(a);
  return w > 180 ? w - 360 : w;
};

export function mat3(): Mat3 {
  const m = new Float64Array(9);
  m[0] = m[4] = m[8] = 1;
  return m;
}

export function mat3Mul(a: Mat3, b: Mat3, out: Mat3 = new Float64Array(9)): Mat3 {
  const r = new Float64Array(9);
  for (let i = 0; i < 3; i++)
    for (let j = 0; j < 3; j++)
      r[i * 3 + j] = a[i * 3] * b[j] + a[i * 3 + 1] * b[3 + j] + a[i * 3 + 2] * b[6 + j];
  out.set(r);
  return out;
}

export function mat3T(a: Mat3, out: Mat3 = new Float64Array(9)): Mat3 {
  const r = new Float64Array(9);
  for (let i = 0; i < 3; i++) for (let j = 0; j < 3; j++) r[i * 3 + j] = a[j * 3 + i];
  out.set(r);
  return out;
}

export function mat3Apply(m: Mat3, x: number, y: number, z: number, out: Vec3 = [0, 0, 0]): Vec3 {
  const ox = m[0] * x + m[1] * y + m[2] * z;
  const oy = m[3] * x + m[4] * y + m[5] * z;
  const oz = m[6] * x + m[7] * y + m[8] * z;
  out[0] = ox;
  out[1] = oy;
  out[2] = oz;
  return out;
}

export function rotX(a: number): Mat3 {
  const c = Math.cos(a), s = Math.sin(a);
  return Float64Array.of(1, 0, 0, 0, c, -s, 0, s, c);
}
export function rotY(a: number): Mat3 {
  const c = Math.cos(a), s = Math.sin(a);
  return Float64Array.of(c, 0, s, 0, 1, 0, -s, 0, c);
}
export function rotZ(a: number): Mat3 {
  const c = Math.cos(a), s = Math.sin(a);
  return Float64Array.of(c, -s, 0, s, c, 0, 0, 0, 1);
}

export const dot = (a: Vec3, b: Vec3) => a[0] * b[0] + a[1] * b[1] + a[2] * b[2];
export const cross = (a: Vec3, b: Vec3): Vec3 => [
  a[1] * b[2] - a[2] * b[1],
  a[2] * b[0] - a[0] * b[2],
  a[0] * b[1] - a[1] * b[0],
];
export const len = (a: Vec3) => Math.hypot(a[0], a[1], a[2]);
export function norm(a: Vec3): Vec3 {
  const l = len(a) || 1;
  return [a[0] / l, a[1] / l, a[2] / l];
}
export const scale = (a: Vec3, s: number): Vec3 => [a[0] * s, a[1] * s, a[2] * s];
export const add = (a: Vec3, b: Vec3): Vec3 => [a[0] + b[0], a[1] + b[1], a[2] + b[2]];
export const sub = (a: Vec3, b: Vec3): Vec3 => [a[0] - b[0], a[1] - b[1], a[2] - b[2]];

export function raDecToVec(raDeg: number, decDeg: number): Vec3 {
  const ra = raDeg * DEG, dec = decDeg * DEG;
  const c = Math.cos(dec);
  return [c * Math.cos(ra), c * Math.sin(ra), Math.sin(dec)];
}

export function vecToRaDec(v: Vec3): { ra: number; dec: number } {
  const l = len(v) || 1;
  return { ra: wrap360(Math.atan2(v[1], v[0]) * RAD), dec: Math.asin(clamp(v[2] / l, -1, 1)) * RAD };
}

/** Altitude/azimuth (degrees, azimuth clockwise from north) to an ENU unit vector. */
export function altAzToEnu(altDeg: number, azDeg: number): Vec3 {
  const alt = altDeg * DEG, az = azDeg * DEG;
  const c = Math.cos(alt);
  return [c * Math.sin(az), c * Math.cos(az), Math.sin(alt)];
}

export function enuToAltAz(v: Vec3): { alt: number; az: number } {
  const l = len(v) || 1;
  return { alt: Math.asin(clamp(v[2] / l, -1, 1)) * RAD, az: wrap360(Math.atan2(v[0], v[1]) * RAD) };
}

/** Saemundsson refraction lift in degrees for a true altitude, matching Astronomy Engine "normal". */
export function refractionLift(altDeg: number): number {
  if (altDeg < -90 || altDeg > 90) return 0;
  const hd = Math.max(altDeg, -1);
  let r = 1.02 / Math.tan((hd + 10.3 / (hd + 5.11)) * DEG) / 60;
  if (altDeg < -1) r *= (altDeg + 90) / 89;
  return r;
}

/** Apply refraction to an ENU unit vector (returns the apparent direction). */
export function refractEnu(v: Vec3): Vec3 {
  const alt = Math.asin(clamp(v[2], -1, 1)) * RAD;
  const a2 = (alt + refractionLift(alt)) * DEG;
  const h = Math.hypot(v[0], v[1]);
  if (h < 1e-9) return [v[0], v[1], v[2]];
  const c = Math.cos(a2) / h;
  return [v[0] * c, v[1] * c, Math.sin(a2)];
}

/** Undo refractEnu: the true direction behind an apparent one (what a tap on the sky points at). */
export function unrefractEnu(v: Vec3): Vec3 {
  const app = Math.asin(clamp(v[2], -1, 1)) * RAD;
  let alt = app;
  for (let i = 0; i < 5; i++) alt = app - refractionLift(alt);
  const h = Math.hypot(v[0], v[1]);
  if (h < 1e-9) return [v[0], v[1], v[2]];
  const c = Math.cos(alt * DEG) / h;
  return [v[0] * c, v[1] * c, Math.sin(alt * DEG)];
}

// ---- Quaternions (x, y, z, w), used for sensor smoothing ----
export type Quat = [number, number, number, number];

export function quatFromMat3(m: Mat3): Quat {
  const tr = m[0] + m[4] + m[8];
  let x, y, z, w;
  if (tr > 0) {
    const s = Math.sqrt(tr + 1) * 2;
    w = 0.25 * s;
    x = (m[7] - m[5]) / s;
    y = (m[2] - m[6]) / s;
    z = (m[3] - m[1]) / s;
  } else if (m[0] > m[4] && m[0] > m[8]) {
    const s = Math.sqrt(1 + m[0] - m[4] - m[8]) * 2;
    w = (m[7] - m[5]) / s;
    x = 0.25 * s;
    y = (m[1] + m[3]) / s;
    z = (m[2] + m[6]) / s;
  } else if (m[4] > m[8]) {
    const s = Math.sqrt(1 + m[4] - m[0] - m[8]) * 2;
    w = (m[2] - m[6]) / s;
    x = (m[1] + m[3]) / s;
    y = 0.25 * s;
    z = (m[5] + m[7]) / s;
  } else {
    const s = Math.sqrt(1 + m[8] - m[0] - m[4]) * 2;
    w = (m[3] - m[1]) / s;
    x = (m[2] + m[6]) / s;
    y = (m[5] + m[7]) / s;
    z = 0.25 * s;
  }
  const l = Math.hypot(x, y, z, w);
  return [x / l, y / l, z / l, w / l];
}

export function mat3FromQuat(q: Quat, out: Mat3 = new Float64Array(9)): Mat3 {
  const [x, y, z, w] = q;
  out[0] = 1 - 2 * (y * y + z * z);
  out[1] = 2 * (x * y - z * w);
  out[2] = 2 * (x * z + y * w);
  out[3] = 2 * (x * y + z * w);
  out[4] = 1 - 2 * (x * x + z * z);
  out[5] = 2 * (y * z - x * w);
  out[6] = 2 * (x * z - y * w);
  out[7] = 2 * (y * z + x * w);
  out[8] = 1 - 2 * (x * x + y * y);
  return out;
}

export function quatSlerp(a: Quat, b: Quat, t: number): Quat {
  let [bx, by, bz, bw] = b;
  let d = a[0] * bx + a[1] * by + a[2] * bz + a[3] * bw;
  if (d < 0) {
    d = -d;
    bx = -bx; by = -by; bz = -bz; bw = -bw;
  }
  let s0: number, s1: number;
  if (d > 0.9995) {
    s0 = 1 - t;
    s1 = t;
  } else {
    const th = Math.acos(d), sn = Math.sin(th);
    s0 = Math.sin((1 - t) * th) / sn;
    s1 = Math.sin(t * th) / sn;
  }
  const r: Quat = [s0 * a[0] + s1 * bx, s0 * a[1] + s1 * by, s0 * a[2] + s1 * bz, s0 * a[3] + s1 * bw];
  const l = Math.hypot(r[0], r[1], r[2], r[3]);
  return [r[0] / l, r[1] / l, r[2] / l, r[3] / l];
}

export function quatAngle(a: Quat, b: Quat): number {
  const d = Math.abs(a[0] * b[0] + a[1] * b[1] + a[2] * b[2] + a[3] * b[3]);
  return 2 * Math.acos(Math.min(1, d));
}
